import { createRoute, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { toast } from '@eureca/ui';
import { authenticatedRoute } from '@/router/authenticated-route';
import { requireWorkspace } from '@/router/require-workspace';
import { mePermissionsQueryOptions } from '@/shared/me-permissions-query';
import FormsListPage from '@/features/forms/forms-list/FormsListPage';
import FormCreatePage from '@/features/forms/form-create/FormCreatePage';
import FormEditPage from '@/features/forms/form-edit/FormEditPage';

/**
 * `/formularios*` URL state. `companyId` is the selected área de trabalho — the
 * list guard requires it (entered from the Home), the page seeds its company
 * filter + logo from it, and the create/edit sub-routes carry it so a "Voltar"
 * lands back on the list without tripping the workspace guard. The in-page
 * "Por empresa / Compartilhados" toggle still lets the consultant widen the
 * scope to company-less shared forms.
 */
export const FormsSearchSchema = z.object({
  companyId: z.string().uuid().optional().catch(undefined),
});
export type FormsSearch = z.infer<typeof FormsSearchSchema>;

/**
 * EU-454 + DEV-93 route guard: creating/editing a Form needs a Keycloak realm
 * role — `gestor-conta` or `specialist`, with an `admin` bypass.
 * The authority is the backend — the guard only reflects `GET /me/permissions`.
 * Non-managers (and permission-validation failures — fail closed, RN-05) are
 * bounced to the list with a toast (mirrors the app's redirect-to-parent pattern
 * used by program-edit; there is no dedicated 403 screen in the SPA). The
 * workspace `companyId` is preserved in the redirect so the list stays scoped.
 */
async function requireFormManager({
  context,
  search,
}: {
  context: { query: QueryClient };
  search: FormsSearch;
}) {
  let outcome: 'ok' | 'denied' | 'error';
  try {
    const perms = await context.query.ensureQueryData(mePermissionsQueryOptions());
    outcome = perms.canManageForms ? 'ok' : 'denied';
  } catch {
    outcome = 'error';
  }
  if (outcome !== 'ok') {
    toast.error(
      outcome === 'denied'
        ? 'Você não tem permissão para criar e editar formulários.'
        : 'Não foi possível validar sua permissão. Tente novamente.'
    );
    throw redirect({ to: '/formularios', search: { companyId: search.companyId } });
  }
}

export const formsListRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/formularios',
  validateSearch: FormsSearchSchema,
  // App Consultor: require a selected company/área de trabalho (EU-374).
  beforeLoad: requireWorkspace,
  component: FormsListPage,
});

export const formCreateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/formularios/novo',
  validateSearch: FormsSearchSchema,
  beforeLoad: requireFormManager,
  component: FormCreatePage,
});

export const formEditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/formularios/$formId/editar',
  validateSearch: FormsSearchSchema,
  beforeLoad: requireFormManager,
  component: FormEditPage,
});
